import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useNavigateWithTransition } from '@muzly/shared';

import { readReturnTo } from '../../../core';
import {
  isProfileComplete,
  loadProfile,
  purgeLegacyStorage,
} from '../../profile/profile';
import {
  NicknameStep,
  StepShell,
  SurveyDoneStep,
  SurveyStep,
} from '../components';
import { OnboardingProvider, useOnboarding } from '../context';
import { SETUP_FLOW } from '../flow';
import { completeSetup } from '../setupCompletion';
import { isSurveyStepAnswered, surveyStepById } from '../survey';

/**
 * 초기 설정(시안 `초기 설정 프로세스` 8단계 + 완료 안내) — 앱 첫 실행이 강제로 여는
 * 전체화면. 프로필이 없는 동안 게이트(`RequireProfile`)가 모든 길을 여기로 보낸다.
 *
 * 나갈 문이 없다(`SETUP_FLOW.closable = false`): 프로필 없이는 홈이 없다.
 * 완료 안내의 CTA가 유일한 출구다 — 마지막 `next`가 `onComplete`로 프로필을 남기고 홈으로.
 * 게이트가 원래 목적지를 실어 보냈으면(딥링크로 들어온 경우) 홈 대신 그리로 간다.
 *
 * 프로필이 이미 완성돼 있으면 열지 않는다 — 더보기의 프로필 수정이 그 일을 한다. 옛
 * 드래프트 이관도 여기서 한 번 더 부른다: `/setup`은 게이트 밖이라 직접 열면 게이트를
 * 안 거친다.
 */

const SetupSteps = () => {
  const { step, answers } = useOnboarding();
  const survey = surveyStepById(step);
  const canAdvance = survey ? isSurveyStepAnswered(survey, answers) : true;

  return (
    <StepShell
      canAdvance={canAdvance}
      // 설문 단계의 CTA는 시안대로 `계속`, 완료 안내는 `다음`(시안 `758:20959`).
      ctaLabelKey={survey ? 'web.common.continue_' : 'web.common.next'}
    >
      {survey ? (
        // 닉네임만 옵션이 아니라 자유입력 + 언어 선택이다 — 렌더가 다르고, 검증(canAdvance)은
        // 같은 설문 seam(isSurveyStepAnswered)을 그대로 탄다.
        survey.kind === 'text' ? (
          <NicknameStep config={survey} />
        ) : (
          <SurveyStep config={survey} />
        )
      ) : (
        <SurveyDoneStep />
      )}
    </StepShell>
  );
};

export const SetupPage = () => {
  const navigate = useNavigateWithTransition();
  const returnTo = readReturnTo(useLocation().state);
  // 열 때 한 번만 본다 — 완료는 이 화면 안에서 일어나고 그때 스스로 나간다.
  const [alreadyDone] = useState(() => {
    purgeLegacyStorage();
    return isProfileComplete(loadProfile()?.answers ?? {});
  });
  if (alreadyDone) {
    return <Navigate to={returnTo ?? '/'} replace />;
  }
  return (
    <OnboardingProvider
      flow={SETUP_FLOW}
      onComplete={(answers) => {
        completeSetup(answers);
        if (returnTo) {
          navigate(returnTo, { replace: true, transition: true });
        } else {
          navigate('/', { replace: true, transition: true, direction: 'back' });
        }
      }}
    >
      <SetupSteps />
    </OnboardingProvider>
  );
};
